import { useState } from "react";
import { Image } from "expo-image";
import { Platform, Pressable, StyleSheet, View } from "react-native";

import {
  AppText,
  Button,
  ButtonText,
  EmptyState,
  Stack,
} from "~/components/ui";
import {
  captureImage,
  pickImage,
  useSignedUrl,
  uploadImage,
} from "~/features/media/storage";
import { useAuth } from "~/hooks/useAuth";
import { usePlan } from "~/hooks/usePlan";
import { usePlantActions, usePlantPhotos } from "~/state/plants";
import {
  radii,
  spacing,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";

function PhotoTile({
  storagePath,
  isCover,
  onSetCover,
  onRemove,
}: {
  storagePath: string;
  isCover: boolean;
  onSetCover: () => void;
  onRemove: () => void;
}) {
  const stylesheet = useThemedStyles(makeStyles);
  const signed = useSignedUrl("plant-photos", storagePath);

  return (
    <View style={stylesheet.tile}>
      {signed.data ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={isCover ? "Cover photo" : "Make cover photo"}
          onPress={onSetCover}
        >
          <Image
            source={{ uri: signed.data }}
            style={stylesheet.image}
            contentFit="cover"
            accessibilityLabel="Plant photo"
          />
        </Pressable>
      ) : (
        <View style={[stylesheet.image, stylesheet.imagePlaceholder]}>
          <AppText size="xs">
            {signed.isLoading ? "Loading" : "Offline"}
          </AppText>
        </View>
      )}
      <View style={stylesheet.tileActions}>
        {isCover ? (
          <AppText size="xs" weight="semibold">
            Cover
          </AppText>
        ) : (
          <Button variant="text" compact onPress={onSetCover}>
            <ButtonText>Set cover</ButtonText>
          </Button>
        )}
        <Button variant="text" compact onPress={onRemove}>
          <ButtonText>Remove</ButtonText>
        </Button>
      </View>
    </View>
  );
}

export function PlantPhotos({
  plantId,
  coverPhotoPath,
}: {
  plantId: string;
  coverPhotoPath: string | null;
}) {
  const stylesheet = useThemedStyles(makeStyles);
  const { user } = useAuth();
  const plan = usePlan();
  const photos = usePlantPhotos(plantId);
  const actions = usePlantActions();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const allowance = plan.photosFor(photos.length);

  async function add(source: "library" | "camera") {
    if (!user) return;
    setError(null);
    setBusy(true);
    try {
      const image =
        source === "camera" ? await captureImage() : await pickImage();
      if (!image) return;
      const path = await uploadImage("plant-photos", user.id, image);
      actions.addPhoto(plantId, path);
    } catch (uploadError) {
      setError(
        uploadError instanceof Error
          ? uploadError.message
          : "Unable to add that photo. Photos need a connection to upload.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack gap="md">
      {photos.length === 0 ? (
        <EmptyState
          title="No photos yet"
          description={
            allowance.allowed
              ? "Photos are the fastest way to remember which plant is which."
              : "Upgrade to Florafolio Pro to attach photos to this plant."
          }
        />
      ) : (
        <View style={stylesheet.grid}>
          {photos.map((photo) => (
            <PhotoTile
              key={photo.id}
              storagePath={photo.storage_path}
              isCover={photo.storage_path === coverPhotoPath}
              onSetCover={() =>
                actions.setCoverPhoto(plantId, photo.storage_path)
              }
              onRemove={() => actions.removePhoto(photo.id)}
            />
          ))}
        </View>
      )}

      {error ? (
        <AppText size="xs" tone="danger">
          {error}
        </AppText>
      ) : null}

      {allowance.allowed ? (
        <View style={stylesheet.actions}>
          <Button
            variant="outline"
            iconName="image"
            disabled={busy}
            onPress={() => void add("library")}
          >
            <ButtonText>
              {busy ? "Uploading..." : "Add from library"}
            </ButtonText>
          </Button>
          {Platform.OS !== "web" ? (
            <Button
              variant="outline"
              iconName="camera"
              disabled={busy}
              onPress={() => void add("camera")}
            >
              <ButtonText>Take a photo</ButtonText>
            </Button>
          ) : null}
        </View>
      ) : (
        <AppText size="xs">{allowance.message}</AppText>
      )}
    </Stack>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    grid: { flexDirection: "row", flexWrap: "wrap", gap: spacing.m },
    tile: { width: 160, gap: spacing.xs },
    image: {
      width: 160,
      height: 160,
      borderRadius: radii.m,
      backgroundColor: colors.surface,
    },
    imagePlaceholder: { alignItems: "center", justifyContent: "center" },
    tileActions: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.xs,
    },
    actions: { flexDirection: "row", flexWrap: "wrap", gap: spacing.s },
  });
